// Raijin GT-9: a low coupe. Numbers in hd px (see docs/CAR_ART.md). Long hood, teardrop cabin set back, high rear haunches, light bar across the tail.
import { K, dk, lt } from '../hd-kit';
import { registerCarModel, type CarModel, type V3 } from '../car-drawn';

// nose low, hood rising to a high shoulder over the rear wheels
const BODY: [number, number][] = [[-24, 2.4], [24, 2.4], [24, 5], [19, 6], [8, 7.6], [-2, 8.2], [-12, 9.2], [-21, 9.8], [-24, 8]];
// long low greenhouse: windscreen raked ~8, rear glass runs down into the deck
const CABIN: [number, number][] = [[-17, 8.4], [5, 8.4], [-3, 12.6], [-12, 12.4]];
const HW = 11.6, HWC = 8;
// wrecked: the tail is crushed down and in (rear face 2.4..6, the deck sags), same edge count
const WBODY: [number, number][] = [[-24, 2.4], [24, 2.4], [24, 5], [19, 6], [8, 7.6], [-2, 8.2], [-12, 9], [-19, 8.6], [-21.6, 6]];
const WCABIN: [number, number][] = [[-17, 8.4], [5, 8.4], [-3, 12.4], [-12, 11.6]];

export const sports: CarModel = {
  body: BODY, wreckedBody: WBODY, cabin: CABIN, wreckedCabin: WCABIN, hw: HW, hwc: HWC, axles: [15.5, -14.5], wheelR: 4.4, archTop: 7.6,
  bodyEdge(i, P) {
    const { fill, endRect } = P;
    if (P.wrecked) {
      if (i === 8) return () => { // crushed tail: diffuser torn, light bar dark and split
        fill(endRect(-21.6, -HW, HW, 2.4, 3.4), K.ink); fill(endRect(-21.6, -HW + 1, -1, 3.8, 5.2), K.ink); fill(endRect(-21.6, 2.5, HW - 1, 4, 5), K.ink);
        fill(endRect(-21.6, -HW + 1.6, -2, 4.1, 4.8), K.metalLo);
      };
      if (i === 7) return () => { // the torn spoiler: one end still bolted on, the other dragging up and out
        const z = 8.8; fill([[-19.6, -HW + 1, z], [-21, -HW + 1, z], [-21.4, 2, z + 2.6], [-19.8, 2, z + 2.6]], K.ink); fill([[-19.9, -HW + 1.5, z + .1], [-20.6, -HW + 1.5, z + .1], [-20.9, 1.4, z + 2.5], [-20.1, 1.4, z + 2.5]], K.metal);
        fill([[-19.4, 4, z - .3], [-20.8, 4, z - .3], [-20.8, 8, z - .5], [-19.4, 8, z - .5]], K.woodLo); // scorched deck
      };
    }
    if (i === 1) return () => { // low nose: splitter, intake, slim headlamps
      fill(endRect(24, -HW, HW, 2.4, 3.4), K.ink);
      fill(endRect(24, -6, 6, 3.4, 4.8), K.ink); for (let v = -5; v < 5; v += 2) fill(endRect(24, v, v + 1, 3.7, 4.6), K.metalLo);
      for (const s of [1, -1]) { fill(endRect(24, s * 7, s * 11.2, 3.6, 4.9), K.ink); fill(endRect(24, s * 7.4, s * 10.8, 3.9, 4.6), P.lampOn ? K.warm : K.metalLo); if (P.lampOn) fill(endRect(24, s * 8, s * 10.2, 4.1, 4.5), K.warmHi); }
    };
    const B = P.wrecked ? WBODY : BODY;
    const onEdge = (k: number, t: number, v: number, dz = .03): V3 => { const a = B[k], b = B[k + 1]; return [a[0] + (b[0] - a[0]) * t, v, a[1] + (b[1] - a[1]) * t + dz]; };
    // No hood stripe: every profile edge is its own outlined face, so a stripe broke into hatch marks at the diagonals and
    // fragments at E/W. The deck carries engine-cover louvres instead.
    if (i === 6) return () => { for (let t = .15; t < .9; t += .2) fill([onEdge(6, t, -7), onEdge(6, t + .08, -7), onEdge(6, t + .08, 7), onEdge(6, t, 7)], dk(P.body, .45)); };
    if (i === 8) return () => { // tail: diffuser and a full-width light bar
      fill(endRect(-24, -HW, HW, 2.4, 3.6), K.ink);
      fill(endRect(-24, -HW + 1, HW - 1, 4.8, 6.6), K.ink);
      fill(endRect(-24, -HW + 1.4, HW - 1.4, 5.1, 6.3), P.lampOn ? K.red : K.metalLo);
      if (P.lampOn) fill(endRect(-24, -HW + 2.4, HW - 2.4, 5.5, 5.9), K.pink);
      fill(endRect(-24, -3, 3, 3.6, 4.6), K.white); fill(endRect(-24, -2.3, 2.3, 3.9, 4.3), K.metalLo); // plate
    };
    return undefined;
  },
  bodySide(s, v, P) {
    const { fill, sideRect, body } = P, seam = dk(body, .8);
    return () => {
      // sculpted, low: a dark lower body (the side scoops in under one long swept shoulder), so the slab reads as two planes
      fill([[-22, v, 3], [21, v, 3], [21, v, 4.6], [6, v, 5.4], [-4, v, 5.6], [-13, v, 6.4], [-22, v, 6.6]], dk(body, .38));
      fill([[-22, v, 6.6], [-13, v, 6.4], [-4, v, 5.6], [6, v, 5.4], [21, v, 4.6], [21, v, 5.1], [6, v, 5.9], [-4, v, 6.2], [-13, v, 7], [-22, v, 7.3]], lt(body, .45)); // the swept shoulder line
      fill(sideRect(v, -4.4, -3.8, 5.6, 8.2), seam); fill(sideRect(v, 6.6, 7.2, 5.4, 7.6), seam); // door seams
      fill(sideRect(v, -2.6, -.6, 6.8, 7.3), K.metalLo); // handle
      fill([[-12.6, v, 4], [-6, v, 4], [-6, v, 5.6], [-11, v, 6]], K.ink); // side intake in the scoop
      fill(sideRect(v, -22, -5, 8.2, 8.9), lt(body, .3)); // haunch top
      if (P.wrecked) { fill(sideRect(v, -20, -12, 4.4, 7.4), s > 0 ? K.woodLo : dk(body, .6)); fill(sideRect(v, 8, 14, 6.2, 6.8), lt(body, .3)); } // scorched haunch, a scrape
    };
  },
  cabinEdge(i, P, { n, at }) {
    const { fill, body } = P;
    if (i === 1) return { color: P.tone(n), decal: () => { // raked windscreen, only when seen from the front
      if (P.toward(n) < .75) return;
      const pane = [at(.1, -HWC + .8), at(.9, -HWC + .8), at(.9, HWC - .8), at(.1, HWC - .8)]; fill(pane, P.glassTone(n));
      if (!P.wrecked) fill([at(.1, -HWC + .8), at(.9, -HWC + .8), at(.9, -HWC + 1.8), at(.1, -HWC + 1.8)], K.glassHi); P.cracks(pane);
    } };
    if (i === 3) return { color: P.tone(n), decal: () => { // rear glass
      if (P.toward(n) < .6) return; const pane = [at(.12, -HWC + 1.2), at(.88, -HWC + 1.2), at(.88, HWC - 1.2), at(.12, HWC - 1.2)]; fill(pane, P.glassTone(n)); P.cracks(pane);
    } };
    return { color: lt(body, .25) }; // roof
  },
  cabinSide(s, v, P) {
    const n: V3 = [0, s, 0], { fill } = P;
    return () => {
      const gt = P.glassTone(n);
      const win: V3[] = [[-15.4, v, 9], [-12, v, 12], [-4.6, v, 12], [3.4, v, 9]];
      fill(win, gt); P.cracks(win);
      if (!P.wrecked) { // light upper edge and a sheen
        fill([[-12, v, 11.2], [-4.6, v, 11.2], [-4.6, v, 12], [-12, v, 12]], K.glassHi);
        fill([[-8, v, 9], [-6.6, v, 9], [-3.6, v, 11.2], [-5, v, 11.2]], K.glassHi);
      }
    };
  },
};
registerCarModel('sports', sports);
